import { Nullable, formatCurrencyFromCents, AvailablePaymentCurrency, TicketingFormCategory } from "@simplyk/common";
import { useController, UseFormReturn } from "react-hook-form";

import { getTicketsPurchasedIndex } from "@/canvas/features/FormV2/helpers/getTicketsPurchasedIndex";
import { useLocaleField } from "@/canvas/features/FormV2/helpers/useLocaleField";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isMin } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { RateOutput } from "@/common/types/trpc";
import { FormAmountField } from "@/forms-design-system/FormAmountField";
import { FormTypography } from "@/forms-design-system/FormTypography";

export type PayWhatYouCanInputProps = {
  rateId: string;
  rates: RateOutput[];
  minimumAmountPerTicket?: Nullable<number>;
  suggestedAmountPerTicket?: Nullable<number>;
  disabled?: boolean;
  form: UseFormReturn<TicketingPaymentInput>;
  formCurrency: AvailablePaymentCurrency;
  purchasedNumber: number;
  isGroupTicket: boolean;
  formCategory: TicketingFormCategory;
};

export const PayWhatYouCanInput = ({
  rateId,
  rates,
  minimumAmountPerTicket,
  suggestedAmountPerTicket,
  disabled,
  form,
  formCurrency,
  purchasedNumber,
  isGroupTicket,
  formCategory,
}: PayWhatYouCanInputProps) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();
  const getLocaleField = useLocaleField();

  const { control, register } = form;
  const { errors } = form.formState;
  const ticketsPurchasedIndex = getTicketsPurchasedIndex(rates, rateId);
  const rateTitle = getLocaleField(rates.find((rate) => rate.id === rateId)?.rateFields)?.title;

  const minimumAmount = minimumAmountPerTicket ? minimumAmountPerTicket * purchasedNumber : undefined;
  const suggestedAmount = suggestedAmountPerTicket ? suggestedAmountPerTicket * purchasedNumber : undefined;

  const formattedMinimumAmount = minimumAmount
    ? formatCurrencyFromCents(minimumAmount, { isoLocale, currency: formCurrency })
    : undefined;

  const formattedSuggestedAmount = suggestedAmount
    ? formatCurrencyFromCents(suggestedAmount, { isoLocale, currency: formCurrency })
    : undefined;

  const {
    field: {
      ref: pwycAmountFieldRef,
      value: pwycAmountFieldValue,
      onChange: onPwycAmountFieldChange,
      ...pwycAmountField
    },
  } = useController<TicketingPaymentInput, `ticketsPurchased.${number}.pwycAmount`>({
    control,
    name: `ticketsPurchased.${ticketsPurchasedIndex}.pwycAmount`,
    rules: minimumAmount
      ? {
          required: t("ticketing", "payWhatYouCanInputRequired"),
          ...isMin(
            minimumAmount,
            t(
              "ticketing",
              purchasedNumber === 1 ? "payWhatYouCanInputMinError_OneTicket" : "payWhatYouCanInputMinError",
              { amount: formattedMinimumAmount, purchasedNumber }
            )
          ),
        }
      : {
          ...isMin(0, t("ticketing", "payWhatYouCanInputNegativeError")),
        },
  });

  const getPlaceholder = () => {
    if (suggestedAmount) {
      return t(
        "ticketing",
        purchasedNumber === 1
          ? "payWhatYouCanInputPlaceholderSuggested_OneTicket"
          : "payWhatYouCanInputPlaceholderSuggested",
        { amount: formattedSuggestedAmount, purchasedNumber }
      );
    }

    if (minimumAmountPerTicket) {
      return t(
        "ticketing",
        purchasedNumber === 1
          ? "payWhatYouCanInputPlaceholderMinimum_OneTicket"
          : "payWhatYouCanInputPlaceholderMinimum",
        { amount: formattedMinimumAmount, purchasedNumber }
      );
    }

    return t("ticketing", "payWhatYouCanInputPlaceholder");
  };

  const fieldError = errors?.ticketsPurchased?.[ticketsPurchasedIndex]?.pwycAmount?.message;
  const amountPerTicket = pwycAmountFieldValue ? pwycAmountFieldValue / (purchasedNumber || 1) : 0;
  const hasMultipleTicketsPurchased = purchasedNumber > 1;
  const itemType: "ticket" | "item" =
    formCategory === TicketingFormCategory.Shop || formCategory === TicketingFormCategory.Custom ? "item" : "ticket";
  const formattedAmountPerTicket = formatCurrencyFromCents(amountPerTicket, { isoLocale, currency: formCurrency });

  return (
    <>
      <FormAmountField
        {...pwycAmountField}
        inputRef={pwycAmountFieldRef}
        placeholder={getPlaceholder()}
        value={pwycAmountFieldValue ?? null}
        onChange={onPwycAmountFieldChange}
        disabled={disabled}
        error={!!fieldError}
        helperText={fieldError}
        currency={formCurrency}
        inputProps={rateTitle ? { "aria-label": t("common", "a11y.amountFor", { item: rateTitle }) } : undefined}
        data-test="pwyc-amount-input"
      />
      {hasMultipleTicketsPurchased && amountPerTicket > 0 && (
        <FormTypography variant="body2" vibe="brand">
          {itemType === "item"
            ? t("ticketing", "payWhatYouCanInputMultipleShopItemsLabel", { amount: formattedAmountPerTicket })
            : isGroupTicket
              ? t("ticketing", "payWhatYouCanInputMultipleTicketsLabel_bundle", { amount: formattedAmountPerTicket })
              : t("ticketing", "payWhatYouCanInputMultipleTicketsLabel", { amount: formattedAmountPerTicket })}
        </FormTypography>
      )}
      <input
        type="hidden"
        value={rateId || undefined}
        {...register(`ticketsPurchased.${ticketsPurchasedIndex}.rateId`)}
        readOnly
      />
    </>
  );
};
